import type { QueryClient } from '@tanstack/react-query';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import type { MaintenanceState, MaintenanceStatus } from './state';

/**
 * Wire query-cache invalidation to maintenance-state recovery.
 *
 * When maintenance recovers (state transitions INTO `'ready'` from any
 * non-ready status), every cached query is invalidated so failure-state
 * UI refetches fresh data.
 *
 * The comparator is initial-state-agnostic. `prev` is captured from
 * `getState().status` at registration time (whatever the current value
 * is: `'ready'` on a clean boot, or any other status if the dev gate
 * already wrote one before this function was called). The
 * `prev !== 'ready' && next === 'ready'` guard then fires invalidation
 * only on real recovery transitions, never on the initial-load fire,
 * regardless of where `prev` started:
 *
 * - If registration runs BEFORE the dev gate's setState: `prev` starts
 *   as `'ready'`. The dev gate's `'ready' -> non-ready` write fires the
 *   listener; guard is false, no invalidation. A later `non-ready ->
 *   'ready'` transition fires the listener; guard is true, invalidate.
 * - If registration runs AFTER the dev gate already wrote a non-ready
 *   status: `prev` starts as that non-ready value. No listener fire
 *   happens for that pre-registration write (subscribe is fire-on-
 *   change, not fire-on-subscribe). The first transition INTO `'ready'`
 *   that does fire the listener correctly trips the guard.
 *
 * Extracted from `src/index.tsx` so the wiring can be unit-tested
 * without booting the React tree. The function takes plumbing
 * dependencies as arguments rather than reaching into module-level
 * imports, which lets tests substitute a stub QueryClient and a
 * controllable subscribe function.
 *
 * @param queryClient - The TanStack Query client whose cache should be
 *   invalidated on recovery.
 * @param subscribe - Maintenance-state subscribe function. Wires the
 *   internal listener; tests pass a controlled emitter.
 * @param getState - Maintenance-state getter. Tests stub this to drive
 *   the comparator without going through the real state module.
 * @returns The unsubscribe function returned by `subscribe`. Calling it
 *   detaches the listener; useful for HMR cleanup.
 */
export function registerRecoveryInvalidation(
  queryClient: QueryClient,
  subscribe: (fn: () => void) => () => void,
  getState: () => Readonly<MaintenanceState>,
): () => void {
  let prev: MaintenanceStatus = getState().status;
  return subscribe(() => {
    const next = getState().status;
    if (prev !== 'ready' && next === 'ready') {
      // Fire-and-forget: invalidation runs in the background while the
      // listener returns immediately. The explicit .catch prevents an
      // unhandled-rejection event if a queryFn rethrows during the
      // resulting refetch chain; the listener stays attached so a later
      // recovery cycle still invalidates.
      queryClient.invalidateQueries().catch((error: unknown) => {
        logger.warn('Recovery invalidation failed', sanitizeError(error));
      });
    }
    prev = next;
  });
}
